import React, { ComponentPropsWithRef, ElementType, forwardRef, useRef } from 'react'

import { twMerge } from 'tailwind-merge'

import AutomaticTruncationTooltip from 'src/components/Typography/AutomaticTruncationTooltip'
import { useMergeRefs } from 'src/hooks/use-merge-refs'

type Props<TElement extends ElementType> = {
  as?: TElement
  children: React.ReactNode
  className?: string
  htmlFor?: string
  maxLines?: 1 | 2 | 3 // feel free to add more numbers, when typed as "number" it's too dynamic for tailwind to compile properly
  role?: React.HTMLProps<HTMLElement>['role']
  style?: React.CSSProperties
  tabIndex?: React.HTMLProps<HTMLElement>['tabIndex']
}
type TextComponentRef<T extends ElementType> = ComponentPropsWithRef<T>['ref']

const TextBase = forwardRef(
  <TElement extends ElementType = 'p'>(
    props: Props<TElement>,
    propRef: TextComponentRef<TElement>
  ) => {
    const { as: Component = 'p', children, className, maxLines, ...rest } = props

    const internalRef = useRef<HTMLElement>(null)
    const ref = useMergeRefs(propRef, internalRef)

    let maxLinesClassName = ''
    switch (maxLines) {
      case 1:
        maxLinesClassName = 'line-clamp-1'
        break
      case 2:
        maxLinesClassName = 'line-clamp-2'
        break
      case 3:
        maxLinesClassName = 'line-clamp-3'
        break
    }

    return (
      <AutomaticTruncationTooltip
        isEnabled={!!maxLines}
        originalTextChildren={children}
        childrenRef={internalRef}
      >
        <Component
          ref={ref}
          className={twMerge(
            `font-sans font-normal break-words text-neutral-950 ${maxLinesClassName}`,
            className
          )}
          {...rest}
        >
          {children}
        </Component>
      </AutomaticTruncationTooltip>
    )
  }
)

const BodySmall = forwardRef(
  <TElement extends ElementType = 'p'>(props: Props<TElement>, ref: TextComponentRef<TElement>) => {
    const { as = 'p', children, className, ...rest } = props

    return (
      <TextBase as={as} ref={ref} className={twMerge(`text-xs`, className)} {...rest}>
        {children}
      </TextBase>
    )
  }
)

const Body = forwardRef(
  <TElement extends ElementType = 'p'>(props: Props<TElement>, ref: TextComponentRef<TElement>) => {
    const { as = 'p', children, className, ...rest } = props

    return (
      <TextBase as={as} ref={ref} className={twMerge(`text-sm`, className)} {...rest}>
        {children}
      </TextBase>
    )
  }
)
const BodyLarge = forwardRef(
  <TElement extends ElementType = 'p'>(props: Props<TElement>, ref: TextComponentRef<TElement>) => {
    const { as = 'p', children, className, ...rest } = props

    return (
      <TextBase as={as} ref={ref} className={twMerge(`text-base`, className)} {...rest}>
        {children}
      </TextBase>
    )
  }
)

const Label = forwardRef(
  <TElement extends ElementType = 'label'>(
    props: Props<TElement>,
    ref: TextComponentRef<TElement>
  ) => {
    const { as = 'label', children, className, ...rest } = props

    return (
      <TextBase
        as={as}
        ref={ref}
        className={twMerge(`text-sm text-neutral-600`, className)}
        {...rest}
      >
        {children}
      </TextBase>
    )
  }
)

const H1 = forwardRef(
  <TElement extends ElementType = 'h1'>(
    props: Props<TElement>,
    ref: TextComponentRef<TElement>
  ) => {
    const { as = 'h1', children, className, ...rest } = props

    return (
      <TextBase
        as={as}
        ref={ref}
        className={twMerge(`text-2xl font-semibold`, className)}
        {...rest}
      >
        {children}
      </TextBase>
    )
  }
)

const H2 = forwardRef(
  <TElement extends ElementType = 'h2'>(
    props: Props<TElement>,
    ref: TextComponentRef<TElement>
  ) => {
    const { as = 'h2', children, className, ...rest } = props

    return (
      <TextBase as={as} ref={ref} className={twMerge(`text-xl font-semibold`, className)} {...rest}>
        {children}
      </TextBase>
    )
  }
)

const H3 = forwardRef(
  <TElement extends ElementType = 'h3'>(
    props: Props<TElement>,
    ref: TextComponentRef<TElement>
  ) => {
    const { as = 'h3', children, className, ...rest } = props

    return (
      <TextBase as={as} ref={ref} className={twMerge(`text-lg font-semibold`, className)} {...rest}>
        {children}
      </TextBase>
    )
  }
)

const H4 = forwardRef(
  <TElement extends ElementType = 'h4'>(
    props: Props<TElement>,
    ref: TextComponentRef<TElement>
  ) => {
    const { as = 'h4', children, className, ...rest } = props

    return (
      <TextBase as={as} ref={ref} className={twMerge(`text-md font-semibold`, className)} {...rest}>
        {children}
      </TextBase>
    )
  }
)

const Text = Object.assign(TextBase, {
  BodySmall,
  Body,
  BodyLarge,
  Label,
  H1,
  H2,
  H3,
  H4,
})

export const T = Text
export default Text
